/**
 * Pure helpers for the account completion prompt (date of birth + country +
 * first-level region). The server re-validates everything; these only give
 * inline feedback before submitting.
 */

export const EARLIEST_DATE_OF_BIRTH = "1900-01-01";

export const REGION_NAME_MAX_LENGTH = 100;

const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Local calendar date as YYYY-MM-DD. */
export function todayIso(date) {
  const d = date instanceof Date ? date : new Date();
  const pad = (n) => (n < 10 ? "0" + n : "" + n);
  return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
}

export function isCalendarDate(value) {
  const m = typeof value === "string" ? DATE_PATTERN.exec(value) : null;
  if (!m) {
    return false;
  }
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const d = new Date(Date.UTC(year, month - 1, day));
  return (
    d.getUTCFullYear() === year &&
    d.getUTCMonth() === month - 1 &&
    d.getUTCDate() === day
  );
}

/** Subdivision options for a curated country, or null for free-text countries. */
export function subdivisionsFor(countryCode, options) {
  const lists = (options && options.subdivisions) || {};
  const list = countryCode ? lists[countryCode] : null;
  return Array.isArray(list) && list.length > 0 ? list : null;
}

/** "select" (US/CA list), "text" (any other country) or "none" (no country yet). */
export function regionMode(countryCode, options) {
  if (!countryCode) {
    return "none";
  }
  return subdivisionsFor(countryCode, options) ? "select" : "text";
}

export function regionLabel(countryCode) {
  if (countryCode === "US") {
    return "State / Territory";
  }
  if (countryCode === "CA") {
    return "Province / Territory";
  }
  return "Province / State / Region";
}

function normalizeRegionName(value) {
  return typeof value === "string" ? value.trim().replace(/\s+/g, " ") : "";
}

function hasDisallowedCharacters(value) {
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code < 32 || code === 127 || value[i] === "<" || value[i] === ">") {
      return true;
    }
  }
  return false;
}

/**
 * @param {{date_of_birth:string,country_code:string,region_code:string,region_name:string}} values
 * @param {{dobLocked:boolean, options:object, today:string}} context
 * @returns {object} field → message (empty when valid)
 */
export function validateEligibilityForm(values, context) {
  const errors = {};
  const options = context && context.options;
  const today = (context && context.today) || todayIso();

  if (!(context && context.dobLocked)) {
    const dob = (values.date_of_birth || "").trim();
    if (!dob) {
      errors.date_of_birth = "Date of birth is required.";
    } else if (!isCalendarDate(dob) || dob < EARLIEST_DATE_OF_BIRTH) {
      errors.date_of_birth = "Enter a valid date of birth.";
    } else if (dob > today) {
      errors.date_of_birth = "Date of birth cannot be in the future.";
    }
  }

  const countries = (options && options.countries) || [];
  const country = values.country_code || "";
  if (!country) {
    errors.country_code = "Country is required.";
    return errors;
  }
  if (!countries.some((c) => c.code === country)) {
    errors.country_code = "Select a valid country.";
    return errors;
  }

  const list = subdivisionsFor(country, options);
  if (list) {
    if (!list.some((s) => s.code === values.region_code)) {
      errors.region_code =
        country === "US"
          ? "Select your state or territory."
          : "Select your province or territory.";
    }
  } else {
    const name = normalizeRegionName(values.region_name);
    if (!name || name.length > REGION_NAME_MAX_LENGTH || hasDisallowedCharacters(name)) {
      errors.region_name =
        "Enter your province, state or region (up to " + REGION_NAME_MAX_LENGTH + " characters).";
    }
  }

  return errors;
}

/** Request body for account/eligibility/save. DOB is only sent while not on file. */
export function buildEligibilityPayload(values, context) {
  const country = values.country_code || "";
  const curated = !!subdivisionsFor(country, context && context.options);
  const payload = {
    country_code: country,
    region_code: curated ? values.region_code || "" : "",
    region_name: curated ? "" : normalizeRegionName(values.region_name),
  };
  if (!(context && context.dobLocked)) {
    payload.date_of_birth = (values.date_of_birth || "").trim();
  }
  return payload;
}

/** Prompt only when the details can be saved and something is missing. */
export function shouldPrompt(status) {
  return !!status && status.storage_ready === true && status.complete === false;
}

/**
 * Read-only rows for the account page, using the server's human-readable
 * labels. Null when the details cannot be shown (not loaded / not available).
 */
export function eligibilitySummaryRows(status) {
  if (!status || status.storage_ready !== true) {
    return null;
  }
  const d = status.display || {};
  return [
    { key: "date_of_birth", label: "Date of Birth", value: d.date_of_birth || null },
    { key: "country", label: "Country", value: d.country || null },
    { key: "region", label: d.region_label || "Region", value: d.region || null },
  ];
}

/**
 * Stored location is complete (country + valid region), the same condition the
 * server uses to lock it. Returns read-only labels/values, or null when the
 * location can still be entered or completed.
 */
export function lockedLocation(status) {
  if (!status || status.storage_ready !== true || !Array.isArray(status.missing)) {
    return null;
  }
  if (status.missing.indexOf("country_code") !== -1 || status.missing.indexOf("region") !== -1) {
    return null;
  }
  const d = status.display || {};
  if (!d.country || !d.region) {
    return null;
  }
  return { country: d.country, regionLabel: d.region_label || "Region", region: d.region };
}

/** Form values from the owner's stored profile (nothing invented). */
export function initialValues(status) {
  const p = (status && status.profile) || {};
  return {
    date_of_birth: p.date_of_birth || "",
    country_code: p.country_code || "",
    region_code: p.region_code || "",
    region_name: p.region_name || "",
  };
}
